import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ElementType, FC, ReactNode } from "react";
import { ALPHA, EMPTY_STATE, FONT_SIZE, ICON_TILE, INK, LETTER_SPACING, MOTION, NEUTRAL, STATUS_BADGE } from "../../../theme/tokens";

/** How much of the screen the state speaks for. */
export enum EmptyStateSize {
	/** Inside a surface: a grid body, a panel, a popover. */
	Default = "default",
	/** The whole screen is this state (an outage screen, a page with nothing on it): larger tile with a halo, heading-scale title. */
	Page = "page",
}

type Props = {
	icon?: ReactNode;
	title: ReactNode;
	/** Element the title renders as. Default `"p"`; a `Page` state that IS the screen passes `"h1"`. */
	titleComponent?: ElementType;
	/** Short hint on how to populate this surface. */
	hint?: ReactNode;
	/** Supporting content under the hint — a status line, a timestamp — laid out in the text column, before `action`. */
	children?: ReactNode;
	/** Action slot (e.g. a SystemButton) rendered under the hint. */
	action?: ReactNode;
	/** Glyph color for the icon tile. Defaults to a quiet muted ink; pass a status tone (e.g. `success.dark`) for a positive "all done" empty state. */
	iconColor?: string;
	/** Icon-tile background wash. Defaults to a muted brand tint; pass a matching status wash when overriding `iconColor`. */
	iconTint?: string;
	/** Default `EmptyStateSize.Default`. */
	size?: EmptyStateSize;
	/** Root test id; the title gets `{dataTestId}-title`. */
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

type SizeSpec = {
	tile: (typeof ICON_TILE)[keyof typeof ICON_TILE];
	/** Ring around the tile (px); 0 = none. */
	halo: number;
	textMaxWidth: number;
	/** Spacing (theme units) between the title and the hint. */
	textGap: number;
	titleSx: SxProps<Theme>;
	hintSx: SxProps<Theme>;
	rootSx: SxProps<Theme>;
};

const SIZE_SPEC: Record<EmptyStateSize, SizeSpec> = {
	[EmptyStateSize.Default]: {
		tile: ICON_TILE.MD,
		halo: 0,
		textMaxWidth: EMPTY_STATE.TEXT_MAX_WIDTH,
		textGap: 0.5,
		titleSx: { fontWeight: 600, fontSize: FONT_SIZE.TITLE_SM, lineHeight: 1.3, color: INK.SECONDARY },
		hintSx: { fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.45, color: NEUTRAL[500] },
		// Vertical room that still fits inside a DataGrid no-rows overlay without clipping.
		rootSx: { gap: 1.5, px: 3, py: 4 },
	},
	[EmptyStateSize.Page]: {
		tile: ICON_TILE.LG,
		halo: EMPTY_STATE.PAGE_HALO_PX,
		textMaxWidth: EMPTY_STATE.PAGE_TEXT_MAX_WIDTH,
		textGap: 1.25,
		titleSx: (theme: Theme) => ({
			fontFamily: theme.typography.h1.fontFamily,
			fontWeight: 700,
			fontSize: { xs: FONT_SIZE.TITLE_XXL, sm: FONT_SIZE.TITLE_HERO },
			lineHeight: 1.2,
			letterSpacing: LETTER_SPACING.HEADLINE,
			color: INK.PRIMARY,
			textWrap: "balance",
		}),
		// A centred paragraph at reading size: balanced lines keep a one-word last line from dangling under it.
		hintSx: { fontSize: FONT_SIZE.ROW_LABEL, lineHeight: 1.55, color: INK.SECONDARY, textWrap: "pretty" },
		rootSx: { gap: 2.5, px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 } },
	},
};

// Muted (not brand) tint by default so the empty state reads as quiet, not promotional. `iconColor`/`iconTint`
// let a caller opt into a status tone (e.g. a green "all caught up") without forking the layout.
const buildIconTileSx = (color: string, tint: string, spec: SizeSpec): SxProps<Theme> => ({
	position: "relative",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	width: spec.tile.size,
	height: spec.tile.size,
	flexShrink: 0,
	borderRadius: `${spec.tile.size / 4}px`,
	backgroundColor: tint,
	color: color,
	// The halo sits in the margin so the tile keeps its size; the extra margin keeps the ring off the title.
	...(spec.halo > 0 && {
		m: `${spec.halo}px`,
		"&::before": {
			content: '""',
			position: "absolute",
			inset: -spec.halo,
			borderRadius: `${spec.tile.size / 4 + spec.halo}px`,
			backgroundColor: tint,
			opacity: EMPTY_STATE.PAGE_HALO_OPACITY,
		},
	}),
	"& > svg": { position: "relative", fontSize: spec.tile.glyph },
});

// Mount fade: opacity + tiny lift, transform/opacity only; reduced-motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes emptyStateEnter": {
		from: { opacity: 0, transform: "translateY(6px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `emptyStateEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/** Centered "nothing here yet" surface (icon, headline, hint, action). Use instead of a blank grid/table body. */
export const EmptyState: FC<Props> = ({
	icon,
	title,
	titleComponent = "p",
	hint,
	children,
	action,
	iconColor = INK.MUTED,
	iconTint = alpha(STATUS_BADGE.BRAND_FALLBACK_HEX, ALPHA.BRAND_TINT),
	size = EmptyStateSize.Default,
	dataTestId,
	sx,
}) => {
	const spec = SIZE_SPEC[size];
	// Explicit, not truthiness: `children && …` would print a stray `0`, and a `{cond && <X />}` child arrives as `false`.
	const hasChildren = children !== undefined && children !== null && typeof children !== "boolean";
	return (
		<Box
			data-testid={dataTestId}
			sx={[
				ENTRANCE_SX,
				{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					textAlign: "center",
					width: "100%",
					height: "100%",
					minHeight: 0,
				},
				spec.rootSx,
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{icon && <Box sx={buildIconTileSx(iconColor, iconTint, spec)}>{icon}</Box>}
			<Box sx={{ display: "flex", flexDirection: "column", gap: spec.textGap, maxWidth: spec.textMaxWidth }}>
				<Typography component={titleComponent} data-testid={dataTestId && `${dataTestId}-title`} sx={spec.titleSx}>
					{title}
				</Typography>
				{hint && (
					<Typography component="p" sx={spec.hintSx}>
						{hint}
					</Typography>
				)}
			</Box>
			{hasChildren && <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: spec.textMaxWidth }}>{children}</Box>}
			{action && <Box sx={{ mt: 0.5 }}>{action}</Box>}
		</Box>
	);
};
